/**
 * Basit tutanak içerik maddeleri — Prensip Kararı / Karar / Gündem (analiz 1.7–1.9, 4.8–4.10, no-any).
 * Aksiyondan farklı: skor/termin/durum yoktur; yalnız ad (+ Gündem'de açıklama) + <b>Sorumlu(lar)</b>
 * (çoklu; en az 1 zorunlu). Backend MinuteItemDto/MinuteItemInput ile birebir. Tek jenerik editör/dialog/
 * görünüm üç tipi de `kind` ile besler. Kişi tipleri aksiyonla ortak ({@link MinutePerson}/{@link PersonInput}).
 */

import { minutePersonToInput, type MinutePerson, type PersonInput } from '../actions/types'

/** REF_MEETING_DECISION_TYPE id'leri (1 Aksiyon / 2 Prensip / 3 Karar / 4 Gündem). */
export const DECISION_TYPE = { ACTION: 1, PRINCIPLE: 2, DECISION: 3, AGENDA: 4 } as const

/** Basit içerik tipi ayrımı (i18n + davranış anahtarı). */
export type MinuteItemKind = 'agenda' | 'principles' | 'decisions'

/** Kind → karar tipi id eşlemesi (bölüm id'siyle editör seçimi için). */
export const KIND_BY_DECISION_TYPE: Record<number, MinuteItemKind> = {
  [DECISION_TYPE.AGENDA]: 'agenda',
  [DECISION_TYPE.PRINCIPLE]: 'principles',
  [DECISION_TYPE.DECISION]: 'decisions',
}

/** Gündem başlık+açıklama; Prensip/Karar tek metin. */
export function hasDescription(kind: MinuteItemKind): boolean {
  return kind === 'agenda'
}

/**
 * Madde metninin karakter sınırı — **tipe göre**. Backend
 * `com.rdc.ceosuite.meeting.MinuteItemLimits` ile birebir aynıdır (aynı değerler iki katmanda ayrı ayrı
 * yazılıyor; ayrışırsa kullanıcı ekranda yazabildiği bir metinde 400 alır).
 *
 * **Prensip Kararı 2000, Gündem/Karar 250** (kullanıcı kararı 2026-08-19). Prensip kararları gerekçeli,
 * paragraf uzunluğunda metinlerdir; Gündem Başlığı ve Karar kısa etiketlerdir ve sınırları bilinçle
 * değiştirilmedi. Sınırlar **karakter** cinsindendir; kolonun **bayt** genişliği ayrı bir kısıttır
 * (`@MaxBytes`). `MEETING_MINUTES.NAME` 2026-09-15'ten beri `VARCHAR2(4000)`'dir — 2000 karakterlik
 * Türkçe metin 4000 bayt ettiği için 2000 baytlık kolona sığmıyordu (`ORA-12899` → HTTP 500).
 */
export function nameMaxLength(kind: MinuteItemKind): number {
  return kind === 'principles' ? 2000 : 250
}

/**
 * Sorumlu alanının i18n anahtarları — **tipe göre**. Gündem maddesinde alan adı "Gündem Sahibi"dir
 * (kullanıcı isteği 2026-09-18); Prensip Kararı ve Karar maddelerinde "Sorumlu" olarak kalır, orada
 * "gündem sahibi" yanıltıcı olurdu. Excel şablonunun Gündem sayfası da aynı adı taşır
 * (`MinutesTemplateExcelSpecs.AGENDA_OWNER_N`) — iki yüzey aynı kelimeyi kullanmalı.
 */
export function responsibleLabels(kind: MinuteItemKind): { label: string; required: string } {
  return kind === 'agenda'
    ? { label: 'minuteItems.agenda.owner', required: 'minuteItems.agenda.ownerRequired' }
    : { label: 'minuteItems.common.responsible', required: 'minuteItems.common.responsibleRequired' }
}

/** Açıklama alanının sınırı (yalnız Gündem'de kullanılır) — **karakter**; kolon 4000 bayttır. */
export const DESCRIPTION_MAX_LENGTH = 2000

/** Editör satırı (yerel durum). {@link id} mevcut madde (düzenleme); null → yeni. */
export interface MinuteItemRow {
  /** React liste anahtarı (yalnız istemci). */
  key: string
  id: number | null
  /** Madde numarası — yalnız gösterim; yeni satırda boş (sunucu kayıtta üretir). */
  recordNumber: string
  /** Prensip/Karar metni veya Gündem başlığı. */
  name: string
  /** Yalnız Gündem'de kullanılır (Gündem açıklaması). */
  description: string
  /** Sorumlu(lar) — en az 1 zorunlu, sicil bazında tekil. */
  responsibles: MinutePerson[]
  /**
   * Satırın kaynağı — **yalnız istemci** (istek gövdesine girmez). Excel'den aktarılan satırlar
   * `'excel'` işaretlenir; ikinci aktarım yalnız o satırların yerine geçer, elle girilenlere dokunmaz
   * (088/FR-022). İşaretsiz satır elle girilmiş sayılır.
   */
  source?: 'manual' | 'excel'
  /**
   * **Yüklenmeyi bekleyen yerel dosyalar** — yalnız istemci, istek gövdesine girmez (108). Yalnız
   * **Prensip Kararı** satırlarında ve yalnız **Tutanak Oluştur** ekranında dolar: orada kayıt henüz
   * yoktur, dolayısıyla dosya sunucuya gidemez ve kaydetme dönüşünde yüklenir. Toplantı Detay'da satır
   * anında kaydedildiği için dosya doğrudan sunucuya gider ve bu alan kullanılmaz.
   */
  files?: File[]
}

/** POST/PUT gövdesindeki madde (backend MinuteItemInput ile birebir). */
export interface MinuteItemInput {
  id?: number | null
  name: string
  description?: string | null
  /** Sorumlu(lar) — toplantıya bağlı maddede en az 1 zorunlu, sicil bazında tekil. */
  responsibles: PersonInput[]
}

/** Toplantı detayındaki madde (backend MinuteItemDto ile birebir). */
export interface MinuteItem {
  id: number
  recordNumber: string
  name: string
  description: string | null
  responsibles: MinutePerson[]
}

/** Boş yeni satır (yerel key ile). */
export function emptyItemRow(): MinuteItemRow {
  return {
    key: crypto.randomUUID(),
    id: null,
    recordNumber: '',
    name: '',
    description: '',
    responsibles: [],
  }
}

/** Detay maddesi → editör satırı (düzenleme başlangıcı). */
export function itemToRow(i: MinuteItem): MinuteItemRow {
  return {
    key: crypto.randomUUID(),
    id: i.id,
    recordNumber: i.recordNumber,
    name: i.name,
    description: i.description ?? '',
    responsibles: i.responsibles ?? [],
  }
}

/**
 * Editör satırlarını istek payload'ına çevirir. Tümüyle boş satır atlanır; bir alanı dolu ama zorunlusu
 * eksik satır → 'invalid' (çağıran uyarı gösterir). {@code withDescription} (Gündem) → açıklama da zorunlu.
 * <b>En az bir Sorumlu zorunludur</b> (analiz 1.7–1.9; 2026-08-12'de çoklu oldu). Sunucu ayrıca doğrular.
 */
export function buildMinuteItemsPayload(
  rows: MinuteItemRow[],
  withDescription: boolean,
): MinuteItemInput[] | 'invalid' {
  const kept = includedMinuteItemRows(rows, withDescription)
  if (kept === 'invalid') return 'invalid'
  return kept.map((r) => ({
    id: r.id ?? undefined,
    name: r.name.trim(),
    description: withDescription ? r.description.trim() : undefined,
    responsibles: r.responsibles.map(minutePersonToInput),
  }))
}

/**
 * Gövdeye **giren** satırlar — {@link buildMinuteItemsPayload} ile birebir aynı süzgeç, aynı sıra.
 *
 * <p>Neden ayrı bir dışa açık fonksiyon: Tutanak Oluştur'da prensip kararlarının dosyaları kayıt
 * dönüşünde sunucudan gelen id'lerle eşleştiriliyor ve sunucu id'leri **gövdedeki** sırayla veriyor.
 * Boş satırlar gövdeye girmediği için `rows` dizisiyle indis eşlemesi YANLIŞ olurdu (bir boş satır tüm
 * eşlemeyi kaydırır ve dosya başka bir karara yüklenirdi). Süzgeç tek yerde durur ki ikisi ayrışmasın.
 */
export function includedMinuteItemRows(
  rows: MinuteItemRow[],
  withDescription: boolean,
): MinuteItemRow[] | 'invalid' {
  const out: MinuteItemRow[] = []
  for (const r of rows) {
    const nameFilled = r.name.trim() !== ''
    const descFilled = r.description.trim() !== ''
    const anyFilled = nameFilled || (withDescription && descFilled) || r.responsibles.length > 0
    if (!anyFilled) continue
    if (!nameFilled) return 'invalid'
    if (withDescription && !descFilled) return 'invalid'
    // En az bir Sorumlu zorunlu (analiz 1.7–1.9).
    if (r.responsibles.length === 0) return 'invalid'
    out.push(r)
  }
  return out
}
